import React, {
  useContext,
  useRef,
  useState,
  useMemo,
  useLayoutEffect,
} from 'react';
import { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import Container from './Container';
import DragHandle from './DragHandle';

const calculateNext = (value, min, max, stepSize, distance, length) => {
  let val = value + (distance / length) * (max - min);
  val = (val / stepSize) * stepSize;
  if (val < min) {
    val = min;
  }
  if (val > max) {
    val = max;
  }
  return val;
};

const valueAsPercentage = (value, max) => Math.round((value / max) * 100);

const calculateRailStyle = (value, max, isHorizontal) => {
  const percentage = valueAsPercentage(value, max);
  return {
    width: isHorizontal ? `${percentage}%` : 'auto',
    height: isHorizontal ? 'auto' : `${percentage}%`,
  };
};

const calculateDragHandleStyle = (value, max, isHorizontal) => {
  const percentage = valueAsPercentage(value, max);
  return {
    left: isHorizontal ? `${percentage}%` : '0px',
    top: isHorizontal ? '0px' : `${100 - percentage}%`,
  };
};

const Slider = ({
  min,
  max,
  value: defaultValue,
  stepSize,
  orientation,
  backgroundColor: defaultBackgroundColor,
  onChanged,
  ...props
}) => {
  const { primary } = useContext(ThemeContext);
  const backgroundColor =
    defaultBackgroundColor || (primary || {}).normal || 'blue';
  const isHorizontal = orientation === 'horizontal';
  const containerRef = useRef();
  const railRef = useRef();
  const [value, setValue] = useState(defaultValue);

  const onDrag = useMemo(() => {
    return dragArgs => {
      const { state, distanceX, distanceY, element: dragHandle } = dragArgs;
      const { current: container } = containerRef;
      const { current: rail } = railRef;
      const { offsetWidth, offsetHeight } = container;
      const val = calculateNext(
        value,
        min,
        max,
        stepSize,
        isHorizontal ? distanceX : -distanceY,
        isHorizontal ? offsetWidth : offsetHeight
      );
      const { width, height } = calculateRailStyle(val, max, isHorizontal);
      const { left, top } = calculateDragHandleStyle(val, max, isHorizontal);

      rail.style.width = width;
      rail.style.height = height;
      dragHandle.style.left = left;
      dragHandle.style.top = top;

      if (state === 'stopped') {
        setValue(val);
      }

      onChanged({
        value: val,
        dragArgs,
      });
    };
  }, [value, min, max, stepSize, isHorizontal, onChanged]);

  useLayoutEffect(() => setValue(defaultValue), [defaultValue]);

  const dragHandleStyle = useMemo(
    () => calculateDragHandleStyle(value, max, isHorizontal),
    [isHorizontal, max, value]
  );

  const railStyle = useMemo(
    () => calculateRailStyle(value, max, isHorizontal),
    [isHorizontal, max, value]
  );

  return (
    <Container
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={isHorizontal ? '180px' : 'auto'}
      height={isHorizontal ? 'auto' : '180px'}
      {...props}
    >
      <Container
        ref={containerRef}
        position="relative"
        height={isHorizontal ? '3px' : '100%'}
        width={isHorizontal ? '100%' : '3px'}
      >
        <Container
          position="absolute"
          top="0px"
          left="0px"
          right="0px"
          bottom="0px"
          backgroundColor="background800"
          borderRadius="3px"
        />
        <Container
          ref={railRef}
          position="absolute"
          bottom="0px"
          left="0px"
          borderRadius="3px"
          backgroundColor={backgroundColor}
          top={isHorizontal ? '0px' : 'auto'}
          right={isHorizontal ? 'auto' : '0px'}
          style={railStyle}
        />
        <DragHandle onDrag={onDrag} position="absolute" style={dragHandleStyle}>
          <Container
            position="absolute"
            left={isHorizontal ? '-15px' : '-14px'}
            top={isHorizontal ? '-15px' : '-16px'}
            height="30px"
            width="30px"
            border="solid 1px #2962ff"
            boxShadow="0 0 6px 0 rgba(0, 0, 0, 0.5)"
            borderRadius="100px"
            backgroundColor="#fff"
          />
        </DragHandle>
      </Container>
    </Container>
  );
};

Slider.propTypes = {
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  stepSize: PropTypes.number,
  orientation: PropTypes.string,
  backgroundColor: PropTypes.string,
  onChanged: PropTypes.func,
};

Slider.defaultProps = {
  value: 0,
  min: 0,
  max: 100,
  stepSize: 1,
  orientation: 'horizontal',
  backgroundColor: null,
  onChanged: () => {},
};

export default Slider;
